/**
 * FO-058 (batch-18) Deel A — pure controle-service.
 *
 * Vergelijkt de VERWACHTE onderhoudsbezetting per klant (uit het
 * KlantOnderhoudSchema: weekdag + aantal monteurs + cadans) met de WERKELIJKE
 * planning (aantal geplande monteurs per weekdag in de doelweek) en levert een
 * classificatie `NIET_GEPLAND | ONVOLLEDIG | ONJUIST | OK` + een per-dag-
 * breakdown voor de UI.
 *
 * Zuiver (geen DB, geen tijdzone-IO): de job rondom deze functie levert de
 * doelweek-maandag (UTC) en de geplande aantallen aan. De cadans-pariteit (om de
 * week) is deterministisch via een anker, op ISO-weken (week start maandag).
 *
 * De acceptatie-onderdrukking hergebruikt de bestaande dekking-acceptatie:
 * `isAfwijkingGeaccepteerd` spiegelt de EENMALIG/STRUCTUREEL-richtinglogica uit
 * `apps/api/src/routes/planning.ts` (GET /planning/dekking), zodat een planner
 * een afwijking op één plek accepteert en daarna ook deze controle-melding stopt.
 */

import { mondayOfIsoWeek, parseWeekString, weekStringFromDate } from './iso-week'

const DAG_MS = 86_400_000
// Vaste epoch-maandag (1970-01-05, een maandag) voor de om-de-week-pariteit als
// er geen anker is opgegeven. Pariteit op werkelijke week-afstand i.p.v. het
// ISO-weeknummer, zodat de jaarwissel (53-weken-jaar) de pariteit niet breekt.
const EPOCH_MAANDAG_MS = Date.UTC(1970, 0, 5)

/**
 * Cadans-vormen (FO-311). WEKELIJKS geldt elke week; de overige vormen zijn
 * niet-wekelijks en tonen in de UI een "eerstvolgende keer"-datum waaruit het
 * interne anker/nDeWeekdag wordt afgeleid (geen zichtbaar ankerweek-veld meer).
 *  - TWEEWEKELIJKS : legacy-alias, gedraagt zich als OM_DE_N_WEKEN met interval 2.
 *  - OM_DE_N_WEKEN : elke N weken (2-12) vanaf het anker (pariteit op week-afstand).
 *  - MAANDELIJKS   : de n-de {weekdag} van de maand (bv. "eerste dinsdag").
 *  - WEKENSET      : even/oneven ISO-weken, of een vaste set weeknummers.
 */
export type OnderhoudCadansType =
  | 'WEKELIJKS'
  | 'TWEEWEKELIJKS'
  | 'OM_DE_N_WEKEN'
  | 'MAANDELIJKS'
  | 'WEKENSET'

/** Pariteit-keuze voor WEKENSET (even/oneven ISO-weeknummers). */
export type WeekPariteitType = 'EVEN' | 'ONEVEN'

export type OnderhoudControleStatus = 'NIET_GEPLAND' | 'ONVOLLEDIG' | 'ONJUIST' | 'OK'

/** Per-dag classificatie binnen één doelweek. */
export type OnderhoudDagStatus =
  | 'OK'
  | 'ONTBREEKT' // verwacht > 0, niemand gepland
  | 'TE_WEINIG' // 0 < gepland < verwacht
  | 'TE_VEEL' // gepland > verwacht
  | 'ONVERWACHT' // verwacht 0, toch gepland (mogelijk verkeerde dag)

export interface OnderhoudRegelInput {
  /** ISO-weekdag 1-7 (ma=1 .. zo=7). */
  weekdag: number
  aantalMonteurs: number
  cadans: OnderhoudCadansType
  /** Anker voor de pariteit-cadans (OM_DE_N_WEKEN/TWEEWEKELIJKS); ISO-maandag, leidend. */
  ankerDatum: Date | null
  /** ISO-weeknummer-anker (weergave; fallback wanneer ankerDatum ontbreekt). */
  ankerWeek: number | null
  /** OM_DE_N_WEKEN: interval in weken (2-12); default 2. */
  intervalWeken?: number | null
  /** MAANDELIJKS: het hoeveelste voorkomen van de weekdag in de maand (1-5). */
  nDeWeekdag?: number | null
  /** WEKENSET: even/oneven ISO-weken (sluit weekSet uit). */
  weekPariteit?: WeekPariteitType | null
  /** WEKENSET: expliciete ISO-weeknummers 1-53 (sluit weekPariteit uit). */
  weekSet?: number[] | null
  actief?: boolean
}

export interface OnderhoudDagResultaat {
  weekdag: number
  verwacht: number
  gepland: number
  status: OnderhoudDagStatus
}

export interface OnderhoudControleResultaat {
  status: OnderhoudControleStatus
  dagen: OnderhoudDagResultaat[]
  totaalVerwacht: number
  totaalGepland: number
}

/** UTC-maandag (00:00) van de ISO-week waar `d` in valt. */
function isoMaandag(d: Date): Date {
  const { week, year } = parseWeekString(weekStringFromDate(d))
  return mondayOfIsoWeek(week, year)
}

/** ISO-weekdag (ma=1 .. zo=7) van een UTC-datum. */
function isoWeekdagVan(d: Date): number {
  return ((d.getUTCDay() + 6) % 7) + 1
}

/**
 * Geldt een regel in de doelweek? Eén bron voor de cron-toets én de
 * voorvallen-preview (property-getest: {@link komendeVoorvallen} verzamelt exact
 * de weken waarvoor deze functie `true` geeft).
 *  - WEKELIJKS     : elke week.
 *  - TWEEWEKELIJKS : als OM_DE_N_WEKEN met interval 2.
 *  - OM_DE_N_WEKEN : week-afstand tot het anker is een veelvoud van intervalWeken
 *                    (op werkelijke ISO-week-afstand, dus jaargrens-veilig).
 *  - MAANDELIJKS   : de weekdag-dag van deze week is het nDeWeekdag-de voorkomen
 *                    van die weekdag in zijn maand (5e bestaat niet elke maand ->
 *                    die maand telt niet mee; eerlijk overgeslagen).
 *  - WEKENSET      : even/oneven ISO-weeknummer, of weeknummer in weekSet.
 */
export function regelVerwachtInWeek(regel: OnderhoudRegelInput, doelweekMaandag: Date): boolean {
  if (regel.actief === false) return false
  switch (regel.cadans) {
    case 'WEKELIJKS':
      return true
    case 'TWEEWEKELIJKS':
    case 'OM_DE_N_WEKEN':
      return geldtOmDeNWeken(regel, doelweekMaandag)
    case 'MAANDELIJKS':
      return geldtMaandelijks(regel, doelweekMaandag)
    case 'WEKENSET':
      return geldtWekenset(regel, doelweekMaandag)
    default:
      return false
  }
}

/**
 * Interval-cadans: pariteit op de werkelijke afstand in hele weken tussen de
 * anker-maandag en de doelweek-maandag (jaargrens-veilig). Met `ankerDatum`
 * rekenen we vanaf diens ISO-maandag, anders vanaf een vaste epoch-maandag.
 */
function geldtOmDeNWeken(regel: OnderhoudRegelInput, doelweekMaandag: Date): boolean {
  const interval = regel.cadans === 'TWEEWEKELIJKS' ? 2 : regel.intervalWeken ?? 2
  if (!Number.isFinite(interval) || interval < 1) return false
  const doelMaandagMs = isoMaandag(doelweekMaandag).getTime()
  const ankerMaandagMs = regel.ankerDatum ? isoMaandag(regel.ankerDatum).getTime() : EPOCH_MAANDAG_MS
  const wekenVerschil = Math.round((doelMaandagMs - ankerMaandagMs) / (7 * DAG_MS))
  // Genormaliseerde modulo (JS `%` kan negatief zijn bij weken vóór het anker).
  return ((wekenVerschil % interval) + interval) % interval === 0
}

/** Maandelijks: het nDeWeekdag-de voorkomen van de weekdag in de maand. */
function geldtMaandelijks(regel: OnderhoudRegelInput, doelweekMaandag: Date): boolean {
  const nDe = regel.nDeWeekdag ?? 1
  if (nDe < 1 || nDe > 5) return false
  // De dag in deze ISO-week die op de regel-weekdag valt.
  const dagInWeek = new Date(isoMaandag(doelweekMaandag).getTime() + (regel.weekdag - 1) * DAG_MS)
  return nDeWeekdagVanDatum(dagInWeek) === nDe
}

/** Wekenset: even/oneven of expliciete weeknummers. */
function geldtWekenset(regel: OnderhoudRegelInput, doelweekMaandag: Date): boolean {
  const { week } = parseWeekString(weekStringFromDate(doelweekMaandag))
  if (regel.weekPariteit === 'EVEN') return week % 2 === 0
  if (regel.weekPariteit === 'ONEVEN') return week % 2 === 1
  return (regel.weekSet ?? []).includes(week)
}

/** Eén voorval (verwachte week) in de voorvallen-preview. */
export interface OnderhoudVoorval {
  /** UTC-maandag (00:00) van de week. */
  maandag: Date
  /** ISO-weeknummer (1-53). */
  week: number
  /** ISO-weekjaar (kan afwijken van het kalenderjaar rond de jaargrens). */
  jaar: number
}

/**
 * De eerstvolgende `aantal` weken (vanaf `vanafMaandag`, inclusief) waarin de
 * regel geldt. Tweede pure functie op DEZELFDE logica als
 * {@link regelVerwachtInWeek}: de preview toont exact de eerstvolgende cron-hits
 * (property-getest). Itereert per ISO-week vooruit in hele UTC-dagen, dus
 * jaargrens- en DST-veilig. Een veiligheidsplafond voorkomt een oneindige lus
 * als een regel nooit geldt (bv. lege weekSet of nDeWeekdag buiten 1-5).
 */
export function komendeVoorvallen(
  regel: OnderhoudRegelInput,
  vanafMaandag: Date,
  aantal = 6,
): OnderhoudVoorval[] {
  const resultaat: OnderhoudVoorval[] = []
  if (aantal <= 0) return resultaat
  let maandag = isoMaandag(vanafMaandag)
  const maxIteraties = 520 // ~10 jaar vooruit; ruim genoeg voor elke realistische cadans
  for (let i = 0; i < maxIteraties && resultaat.length < aantal; i++) {
    if (regelVerwachtInWeek(regel, maandag)) {
      const { week, year } = parseWeekString(weekStringFromDate(maandag))
      resultaat.push({ maandag: new Date(maandag), week, jaar: year })
    }
    maandag = new Date(maandag.getTime() + 7 * DAG_MS)
  }
  return resultaat
}

/**
 * Het hoeveelste voorkomen van de weekdag-van-`d` in de maand van `d` (1-5).
 * Bv. dinsdag 14 juli -> 2 (tweede dinsdag). Gebruikt om uit de "eerstvolgende
 * keer"-datum de MAANDELIJKS-`nDeWeekdag` af te leiden.
 */
export function nDeWeekdagVanDatum(d: Date): number {
  return Math.floor((d.getUTCDate() - 1) / 7) + 1
}

/**
 * Leidt het interne anker af uit de door de gebruiker gekozen "eerstvolgende
 * keer" (de ISO-maandag van die week). Leidend voor OM_DE_N_WEKEN/TWEEWEKELIJKS;
 * voor de andere vormen puur informatief.
 */
export function ankerVanEerstvolgendeKeer(eerstvolgendeKeer: Date): Date {
  return isoMaandag(eerstvolgendeKeer)
}

/** Valt de gekozen "eerstvolgende keer" op de regel-weekdag (1-7)? */
export function eerstvolgendeKeerOpWeekdag(eerstvolgendeKeer: Date, weekdag: number): boolean {
  return isoWeekdagVan(eerstvolgendeKeer) === weekdag
}

function dagStatus(verwacht: number, gepland: number): OnderhoudDagStatus {
  if (verwacht === 0) return gepland > 0 ? 'ONVERWACHT' : 'OK'
  if (gepland === 0) return 'ONTBREEKT'
  if (gepland < verwacht) return 'TE_WEINIG'
  if (gepland > verwacht) return 'TE_VEEL'
  return 'OK'
}

/**
 * Evalueer één klant/doelweek. `geplandPerWeekdag` is het aantal DISTINCTE
 * geplande monteurs per ISO-weekdag (1-7) bij die klant in de doelweek.
 *
 * Aggregatie-prioriteit:
 *  - geen enkele verwachting deze week           -> OK
 *  - niets gepland op de verwachte dag(en) en ook niet op een andere dag -> NIET_GEPLAND
 *  - werk staat er wél, maar de verwachte dag is leeg (verkeerde dag)     -> ONJUIST
 *  - te veel monteurs of planning op een niet-verwachte dag              -> ONJUIST
 *  - te weinig monteurs of een verwachte dag ontbreekt                   -> ONVOLLEDIG
 *  - alles klopt                                                          -> OK
 */
export function evalueerOnderhoudControle(
  regels: OnderhoudRegelInput[],
  geplandPerWeekdag: Record<number, number>,
  doelweekMaandag: Date,
): OnderhoudControleResultaat {
  const verwachtPerDag = new Map<number, number>()
  for (const regel of regels) {
    if (regel.actief === false) continue
    if (!regelVerwachtInWeek(regel, doelweekMaandag)) continue
    verwachtPerDag.set(regel.weekdag, (verwachtPerDag.get(regel.weekdag) ?? 0) + regel.aantalMonteurs)
  }

  const dagen: OnderhoudDagResultaat[] = []
  let totaalVerwacht = 0
  let totaalGepland = 0
  let hasOntbreekt = false
  let hasTeWeinig = false
  let hasTeVeel = false
  let hasOnverwacht = false
  let verwachteDagen = 0
  let verwachteDagenLeeg = 0

  for (let weekdag = 1; weekdag <= 7; weekdag++) {
    const verwacht = verwachtPerDag.get(weekdag) ?? 0
    const gepland = geplandPerWeekdag[weekdag] ?? 0
    totaalVerwacht += verwacht
    totaalGepland += gepland
    if (verwacht === 0 && gepland === 0) continue
    const status = dagStatus(verwacht, gepland)
    dagen.push({ weekdag, verwacht, gepland, status })
    if (verwacht > 0) {
      verwachteDagen++
      if (gepland === 0) verwachteDagenLeeg++
    }
    if (status === 'ONTBREEKT') hasOntbreekt = true
    else if (status === 'TE_WEINIG') hasTeWeinig = true
    else if (status === 'TE_VEEL') hasTeVeel = true
    else if (status === 'ONVERWACHT') hasOnverwacht = true
  }

  let status: OnderhoudControleStatus
  if (totaalVerwacht === 0) {
    status = 'OK'
  } else if (verwachteDagen > 0 && verwachteDagenLeeg === verwachteDagen && !hasOnverwacht) {
    // Geen enkele verwachte dag bezet en ook nergens anders planning.
    status = 'NIET_GEPLAND'
  } else if (hasOnverwacht && hasOntbreekt) {
    // Werk staat gepland, maar niet op de verwachte dag -> verkeerde dag.
    status = 'ONJUIST'
  } else if (hasTeVeel || hasOnverwacht) {
    status = 'ONJUIST'
  } else if (hasOntbreekt || hasTeWeinig) {
    status = 'ONVOLLEDIG'
  } else {
    status = 'OK'
  }

  return { status, dagen, totaalVerwacht, totaalGepland }
}

export interface DekkingAcceptatieInput {
  type: 'EENMALIG' | 'STRUCTUREEL'
  /** Maandag van de geaccepteerde week (yyyy-mm-dd). */
  weekStartIso: string
  verwacht: number
  gepland: number
}

/**
 * Spiegelt de dekking-acceptatie-onderdrukking uit planning.ts: een EENMALIG
 * geaccepteerde week onderdrukt elke afwijking in die week; een STRUCTUREEL
 * geaccepteerde afwijking onderdrukt alleen afwijkingen in DEZELFDE RICHTING
 * (te weinig vs te veel) als de gesnapshotte afwijking. Eenmalig wint van
 * structureel. `acceptaties` moet op `createdAt` aflopend gesorteerd zijn (de
 * meest recente structurele wint), net als in de dekking-query.
 *
 * Let op: `verwacht`/`gepland` zijn hier monteur-aantallen i.p.v. dekking-
 * dienstdagen; alleen de RICHTING wordt vergeleken, dus de magnitude hoeft niet
 * 1-op-1 te matchen met de dekking-snapshot.
 */
export function isAfwijkingGeaccepteerd(
  acceptaties: DekkingAcceptatieInput[],
  doelweekStartIso: string,
  gepland: number,
  verwacht: number,
): boolean {
  const eenmalig = acceptaties.find(
    (a) => a.type === 'EENMALIG' && a.weekStartIso === doelweekStartIso,
  )
  if (eenmalig) return true
  const structureel = acceptaties.find((a) => a.type === 'STRUCTUREEL')
  if (structureel && gepland < verwacht === structureel.gepland < structureel.verwacht) {
    return true
  }
  return false
}
